import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { folderColors } from 'src/app/dictionaries/folderColors';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';

import { FirebaseUtilitiesService } from './../../services/firebase-utilities.service';
import { UploadHelperService } from './../../services/upload-helper.service';

/**
 * Folder data with nested structure.
 * Each node has a name, id, and optional list of children.
 */
interface FolderNode {
  name: string;
  folderId: string;
  children?: FolderNode[];
  color: string;
  selected?: boolean;
  parentFolder?: string;
}

/** Flat node with expandable and level information */
interface FolderFlatNode {
  expandable: boolean;
  name: string;
  folderId: string;
  level: number;
  color: string;
  selected?: boolean;
  parentFolder?: string;
}

@Component({
    selector: 'app-custom-folders',
    templateUrl: './custom-folders.component.html',
    styleUrls: ['./custom-folders.component.scss'],
    standalone: false
})
export class CustomFoldersComponent implements OnInit {
  @Output() selectFolder = new EventEmitter<{ folderId: string; folderName: string }>();
  @Output() createAndSelect = new EventEmitter<{ folderId: string; folderName: string }>();
  @Input() casename: string;
  customfolderfilter: string;
  @Input() selectedFolder: string;
  @Input() currentFolder: any;
  @Input() compactMode: boolean = false;

  customFolders: any[];
  validation: any;
  customFoldersColor: string;
  selectedFolderId: any;

  // Tree structure properties
  treeControl = new FlatTreeControl<FolderFlatNode>(
    node => node.level,
    node => node.expandable
  );

  private _transformer = (node: FolderNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      folderId: node.folderId,
      level: level,
      color: node.color,
      selected: node.selected,
      parentFolder: node.parentFolder
    };
  };

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    private uploadHelper_$: UploadHelperService,
    private FirebaseUtilities_$: FirebaseUtilitiesService
  ) {
    this.customFoldersColor = folderColors.custom;
  }

  ngOnInit() {
    this.getAllCustomFolders(this.customfolderfilter).then(() => {
      this.buildFolderTree();
    });

    this.validation = {
      value: false,
      text: (inputValue: string) =>
        inputValue === ''
          ? 'Please enter a folder name'
          : 'This folder name is already taken, please try a different one.'
    };
  }

  select_folder(folderObj: { folderId: string; folderName: string }) {
    this.customFolders.forEach(cf => delete cf.selected);
    this.selectFolder.emit(folderObj);
  }

  selectFolderNode(node: FolderFlatNode) {
    // Clear all selections
    this.dataSource.data.forEach(folder => this.clearSelection(folder));

    // Set selection on the clicked node
    this.setSelection(node.folderId, true);

    // Emit the selection
    this.selectFolder.emit({ folderId: node.folderId, folderName: node.name });
  }

  private clearSelection(folder: FolderNode) {
    folder.selected = false;
    if (folder.children) {
      folder.children.forEach(child => this.clearSelection(child));
    }
  }

  private setSelection(folderId: string, selected: boolean) {
    const setSelectionRecursive = (folder: FolderNode) => {
      if (folder.folderId === folderId) {
        folder.selected = selected;
        return true;
      }
      if (folder.children) {
        return folder.children.some(child => setSelectionRecursive(child));
      }
      return false;
    };

    this.dataSource.data.forEach(folder => setSelectionRecursive(folder));
  }

  buildFolderTree() {
    const folderMap = new Map<string, FolderNode>();
    const rootFolders: FolderNode[] = [];

    // First pass: create all folder nodes
    this.customFolders.forEach(folder => {
      const folderNode: FolderNode = {
        name: folder.name,
        folderId: folder.folderId,
        color: folder.color || this.customFoldersColor,
        selected: folder.folderId === this.currentFolder?.folderId,
        parentFolder: folder.parentFolder || '',
        children: []
      };
      folderMap.set(folder.folderId, folderNode);
    });

    // Second pass: build hierarchy
    this.customFolders.forEach(folder => {
      const folderNode = folderMap.get(folder.folderId);
      if (folder.parentFolder && folderMap.has(folder.parentFolder)) {
        const parentNode = folderMap.get(folder.parentFolder);
        parentNode.children.push(folderNode);
      } else {
        rootFolders.push(folderNode);
      }
    });

    // Set the tree data source
    this.dataSource.data = rootFolders;
    this.treeControl.expandAll();

    // Update the data source to trigger change detection
    this.dataSource.data = [...this.dataSource.data];
  }

  async getAllCustomFolders(filter?, userOwnerID?: string) {
    this.customFolders = await this.FirebaseUtilities_$.getCustomFolders(
      this.casename,
      filter ? filter : null,
      userOwnerID
    );
    this.buildFolderTree();
  }

  hasChild = (_: number, node: FolderFlatNode) => node.expandable;

  onSearchChange(searchTerm: string, userOwnerID?: string) {
    this.customfolderfilter = searchTerm;
    this.getAllCustomFolders(searchTerm, userOwnerID);
  }

  async createFolder(folderName: string, predefined: boolean, folderColor, parentFolderId?: string) {
    const folderObject = {
      belongsTo: this.casename,
      children: '',
      color: folderColor,
      forDelete: false,
      folderId: this.uploadHelper_$.getRandomString(20),
      name: folderName,
      parentFolder: parentFolderId || '',
      predefined: predefined,
      type: 'folder'
    };
    return await this.FirebaseUtilities_$.createFolder(folderObject);
  }

  handleCreateFolder(folderName: string, predefined: boolean, folderColor: string, event?) {
    if (event.keyCode === 13 || (event.type === 'click' && event.currentTarget.nodeName === 'BUTTON')) {
      this.FirebaseUtilities_$.checkFolderName(folderName).then(async res => {
        if (res) {
          this.validation.value = true;
        } else {
          // Create the folder and get the folderId from the folderObject we created
          const folderObject = {
            belongsTo: this.casename,
            children: '',
            color: folderColor,
            forDelete: false,
            folderId: this.uploadHelper_$.getRandomString(20),
            name: folderName,
            parentFolder: '',
            predefined: predefined,
            type: 'folder'
          };

          await this.FirebaseUtilities_$.createFolder(folderObject);
          const folderObj = { folderId: folderObject.folderId, folderName };
          this.select_folder(folderObj);
          this.getAllCustomFolders().then(() => {
            this.buildFolderTree();
          });
          this.validation.value = false;
          // Emit the createAndSelect event to close the container and pass the folder
          this.createAndSelect.emit(folderObj);
        }
      });
    } else {
      return false;
    }
  }
}
